import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService, authEvents } from '../services/auth.service';

// Create the Auth context
const AuthContext = createContext(null);

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Initialize auth state on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        // Check if there's a token in localStorage
        const hasToken = authService.initAuth();
        
        if (hasToken) {
          try {
            // Try to get user info directly - this will fail if token is invalid
            const userInfo = await authService.getUserInfo();
            setCurrentUser(userInfo);
            setIsAuthenticated(true);
          } catch (error) {
            // If user info fetch fails, token is likely invalid
            authService.logout();
            setIsAuthenticated(false);
            setCurrentUser(null);
          }
        } else {
          // Check if auth should be skipped in development
          const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';

          if (skipAuth) {
            console.log('[Dev Mode] Auto-login with environment credentials');
            const email = process.env.REACT_APP_MYMA_EMAIL;
            const password = process.env.REACT_APP_MYMA_PASSWORD;

            if (email && password) {
              try {
                const user = await authService.login(email, password);
                setCurrentUser(user);
                setIsAuthenticated(true);
                return;
              } catch (error) {
                console.error('[Dev Mode] Auto-login failed:', error);
                setAuthError('Auto-login failed. Check REACT_APP_MYMA_EMAIL and REACT_APP_MYMA_PASSWORD');
                setIsAuthenticated(false);
                return;
              }
            } else {
              console.error('[Dev Mode] REACT_APP_MYMA_EMAIL and REACT_APP_MYMA_PASSWORD not set');
              setAuthError('Skip auth enabled but credentials not configured');
              setIsAuthenticated(false);
              return;
            }
          }

          // No token and auth not skipped
          setIsAuthenticated(false);
        }
      } catch (error) {
        setAuthError('Failed to initialize authentication');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);
  
  // Listen for session expiry events
  useEffect(() => {
    // Subscribe to session expiry events
    const unsubscribe = authEvents.onSessionExpired(() => {
      setCurrentUser(null);
      setIsAuthenticated(false);
      setSessionExpired(true);
    });
    
    // Cleanup on unmount
    return () => unsubscribe();
  }, []);

  // Login function
  const login = async (username, password) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const user = await authService.login(username, password);
      setCurrentUser(user);
      setIsAuthenticated(true);
      return user;
    } catch (error) {
      setAuthError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setCurrentUser(null);
    setIsAuthenticated(false);
  };
  
  // Session handling
  const resetSession = () => {
    setSessionExpired(false);
  };

  // Context value
  const value = {
    currentUser,
    isAuthenticated,
    isLoading,
    authError,
    sessionExpired,
    login,
    logout,
    resetSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }


  return context;
};

export default AuthContext;